/**
 * DocumentList Component
 *
 * Displays a list of documents with download and delete capabilities.
 */

import { useState } from 'react';
import { type EventDocument } from '../../api/documents';
import { downloadDocument, deleteDocument, triggerDownload } from '../../api/documents';
import { getErrorMessage } from '../../api/client';
import { formatFileSize, getFileIconType, getFileIconColor } from '../../utils/fileFormatting';
import { ConfirmDialog } from '../ui';

interface DocumentListProps {
  documents: EventDocument[];
  canDelete?: boolean;
  onDeleted?: () => void;
  loading?: boolean;
}

export default function DocumentList({
  documents,
  canDelete = false,
  onDeleted,
  loading = false,
}: DocumentListProps) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingDoc, setDeletingDoc] = useState<EventDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async (doc: EventDocument) => {
    setError(null);
    setDownloadingId(doc.id);
    try {
      const blob = await downloadDocument(doc.id);
      const fileName = doc.displayName || doc.fileName;
      triggerDownload(blob, fileName);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deletingDoc) return;

    setIsDeleting(true);
    setError(null);
    try {
      await deleteDocument(deletingDoc.id);
      setDeletingDoc(null);
      onDeleted?.();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
            <div className="w-10 h-10 bg-gray-200 rounded"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <svg
          className="w-12 h-12 mx-auto mb-3 text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p className="text-sm">No documents uploaded</p>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      <div className="space-y-2">
        {documents.map((doc) => {
          const iconType = getFileIconType(doc.mimeType);
          const iconColor = getFileIconColor(iconType);
          const displayName = doc.displayName || doc.fileName;
          const isDownloading = downloadingId === doc.id;

          return (
            <div
              key={doc.id}
              className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileIcon type={iconType} className={`w-10 h-10 ${iconColor}`} />

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate" title={displayName}>
                  {displayName}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(doc.fileSize)} &middot; {formatDate(doc.uploadedAt)} &middot;{' '}
                  {doc.uploader.firstName} {doc.uploader.lastName}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(doc)}
                  disabled={isDownloading}
                  className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors disabled:opacity-50"
                  title="Download"
                >
                  {isDownloading ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                  )}
                </button>

                {canDelete && (
                  <button
                    onClick={() => setDeletingDoc(doc)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Delete"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        isOpen={!!deletingDoc}
        onClose={() => setDeletingDoc(null)}
        onConfirm={handleDelete}
        title="Delete Document"
        message={`Are you sure you want to delete "${deletingDoc?.displayName || deletingDoc?.fileName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
}

// =============================================================================
// File Icon Component
// =============================================================================

type FileIconProps = {
  type: 'pdf' | 'word' | 'excel' | 'powerpoint' | 'image' | 'text' | 'file';
  className?: string;
};

function FileIcon({ type, className = '' }: FileIconProps) {
  const baseClass = `${className}`;

  switch (type) {
    case 'pdf':
      return (
        <svg className={baseClass} fill="currentColor" viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z" />
          <path d="M8.5 13.5c0-.3.2-.5.5-.5h1c.5 0 1 .2 1 .8 0 .5-.4.7-.9.7H9v1h-.5v-2zm.5.7h.5c.3 0 .5-.1.5-.4s-.2-.3-.5-.3H9v.7zM12 13c.5 0 1 .2 1.2.5.2.3.3.8.3 1.3s-.1.9-.3 1.2c-.2.3-.6.5-1.2.5h-1v-3.5h1zm-.5.5v2.5h.5c.4 0 .6-.1.7-.4.1-.2.2-.6.2-1s-.1-.8-.2-1c-.1-.2-.4-.4-.7-.4h-.5zM14 13.5c0-.3.2-.5.5-.5h1.3v.5H15v.8h.7v.5H15v1.2h-.5v-2.5h-.5z" />
        </svg>
      );
    case 'word':
      return (
        <svg className={baseClass} fill="currentColor" viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z" />
          <path d="M8 13h1l.7 3 .7-3h1l-1.2 4h-1L8 13zm4 0h1l.5 2.5.5-2.5h1l-1 4h-1l-1-4z" />
        </svg>
      );
    case 'excel':
      return (
        <svg className={baseClass} fill="currentColor" viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z" />
          <path d="M8 13l1.5 2-1.5 2h1l1-1.5 1 1.5h1l-1.5-2 1.5-2h-1l-1 1.5-1-1.5H8z" />
        </svg>
      );
    case 'image':
      return (
        <svg className={baseClass} fill="currentColor" viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z" />
          <path d="M8.5 14.5a1 1 0 100-2 1 1 0 000 2zM8 17l2-2 1 1 2-2 3 3H8z" />
        </svg>
      );
    default:
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      );
  }
}
